import { Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { PageSection } from "nazare-ui/components/page-section";

import { cn } from "nazare-ui/utils";

import styles from "./header-and-stats.module.css";
import { useSecondaryHeader } from "../../../secondary-header-context";
import { SkeletonBlock } from "../../../skeleton-block";
import {
  useQaActiveRuns,
  useQaAggregates,
  useQaSchedule,
  useQaRunsLiveSync,
  type AggregateWindow,
} from "../../../qa-automation-api";
import { QaStartRunModal } from "./start-run-modal";

type WindowKey = AggregateWindow;

type WindowOption = {
  key: WindowKey;
  label: string;
};

const WINDOW_OPTIONS: WindowOption[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "6m", label: "Last 6 months" },
  { key: "all", label: "All time" },
];

type StatAccent = "green" | "orange" | "red" | "neutral";

type StatCardData = {
  key: string;
  label: string;
  value: string;
  allTime: string;
  accent: StatAccent;
};

const DEFAULT_WINDOW: WindowKey = "30d";

/** Validated set of `WindowKey`s the `window` query param is allowed to
 * decode to — anything else (missing param, stale/typo'd value, a value
 * from a future build this one doesn't know about) falls back to
 * `DEFAULT_WINDOW` rather than rendering no tab as active. */
const WINDOW_KEYS = new Set<WindowKey>(WINDOW_OPTIONS.map((option) => option.key));

function decodeWindow(param: string | null): WindowKey {
  return param && WINDOW_KEYS.has(param as WindowKey) ? (param as WindowKey) : DEFAULT_WINDOW;
}

const ACCENT_CLASS: Record<StatAccent, string> = {
  green: styles.accentGreen,
  orange: styles.accentOrange,
  red: styles.accentRed,
  neutral: styles.accentNeutral,
};

const RELATIVE_TIME = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

function formatRelative(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const diffHours = Math.round(diffMs / (60 * 60 * 1000));
  if (Math.abs(diffHours) < 24) {
    return RELATIVE_TIME.format(diffHours, "hour");
  }
  const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
  return RELATIVE_TIME.format(diffDays, "day");
}

/** "7 Jul 2026, 14:32" style absolute UTC label. */
function formatUtc(iso: string): string {
  return `${new Date(iso).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  })} UTC`;
}

/**
 * "Next scheduled run" / "Runs in progress" info cards, and the four
 * pass/comment/fail/runs KPI cards from the top of the real
 * `/admin/qa-automation` dashboard. The date-range tab bar + "Start new run"
 * action that used to render above these is now published into the app
 * shell's secondary-header slot instead (see `useSecondaryHeader` below).
 * Charts and tables below this belong to sibling components. All figures are
 * fetched from the QA automation API.
 */
export function QaAutomationHeaderAndStats() {
  const navigate = useNavigate();

  // Keep the selected date range in the URL so the dashboard view survives a
  // reload and can be linked directly.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeWindow = useMemo(() => decodeWindow(searchParams.get("window")), [searchParams]);
  const aggregates = useQaAggregates(activeWindow);
  const allTimeAggregates = useQaAggregates("all");
  const schedule = useQaSchedule();
  const activeRuns = useQaActiveRuns();
  useQaRunsLiveSync(activeRuns.data?.runs ?? []);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const isLoading = aggregates.isLoading || allTimeAggregates.isLoading;
  const setActiveWindow = useCallback((key: WindowKey) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === DEFAULT_WINDOW) next.delete("window");
        else next.set("window", key);
        return next;
      },
      { replace: true },
    ), [setSearchParams]);
  const startNewRun = useCallback(() => setStartModalOpen(true), []);

  const activeWindowLabel = useMemo(
    () => WINDOW_OPTIONS.find((option) => option.key === activeWindow)?.label ?? "",
    [activeWindow],
  );

  const tabBarRow = useMemo(
    () => (
      <div className={styles.tabBarRow}>
        <div className={styles.tabBar} role="tablist" aria-label="Date range">
          {WINDOW_OPTIONS.map((option) => {
            const active = option.key === activeWindow;
            return (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={cn(styles.tab, active && styles.tabActive)}
                onClick={() => setActiveWindow(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.startRunButton}
          onClick={startNewRun}
        >
          <Plus size={14} aria-hidden />
          Start new run
        </button>
      </div>
    ),
    [activeWindow, setActiveWindow, startNewRun],
  );

  useSecondaryHeader(tabBarRow);

  const nextRun = schedule.data?.nextRuns[0] ?? null;
  const laterRuns = schedule.data?.nextRuns.slice(1, 3) ?? [];
  const current = aggregates.data?.overall;
  const allTime = allTimeAggregates.data?.overall;
  const passWithComments = current ? Math.max(0, 100 - current.passPct - current.failPct) : 0;
  const allTimePassWithComments = allTime ? Math.max(0, 100 - allTime.passPct - allTime.failPct) : 0;
  const statCards: StatCardData[] = [
    { key: "pass-rate", label: "Pass rate", value: `${(current?.passPct ?? 0).toFixed(1)}%`, allTime: `All time: ${(allTime?.passPct ?? 0).toFixed(1)}%`, accent: "green" },
    { key: "pass-with-comments", label: "Pass w/ comments", value: `${passWithComments.toFixed(1)}%`, allTime: `All time: ${allTimePassWithComments.toFixed(1)}%`, accent: "orange" },
    { key: "fail-rate", label: "Fail rate", value: `${(current?.failPct ?? 0).toFixed(1)}%`, allTime: `All time: ${(allTime?.failPct ?? 0).toFixed(1)}%`, accent: "red" },
    { key: "runs", label: "Runs", value: String(current?.runs ?? 0), allTime: `All time: ${allTime?.runs ?? 0}`, accent: "neutral" },
  ];

  return (
    <div className={styles.root}>
      {startModalOpen && <QaStartRunModal onClose={() => setStartModalOpen(false)} onStarted={(runId) => navigate(`/qa-automation/run/${runId}`)} />}
      <div className={styles.infoRow}>
        <PageSection title="Next scheduled run" padded>
          {schedule.isLoading ? (
            <>
              <SkeletonBlock width="58%" height={16} />
              <SkeletonBlock width="38%" height={12} className={styles.infoSecondarySkeleton} />
            </>
          ) : (
            <>
              <p className={styles.infoPrimary}>
                {nextRun ? formatRelative(nextRun) : "No scheduled run"}
                {nextRun && <span className={styles.infoMuted}> · {formatUtc(nextRun)}</span>}
              </p>
              {laterRuns.length > 0 && (
                <p className={styles.infoSecondary}>
                  Then:{" "}
                  {laterRuns.map((iso, index) => (
                    <span key={iso}>
                      {index > 0 && ", "}
                      {formatRelative(iso)}
                    </span>
                  ))}
                </p>
              )}
            </>
          )}
        </PageSection>

        <PageSection title="Runs in progress" padded>
          {activeRuns.isLoading ? (
            <SkeletonBlock width="44%" height={12} className={styles.infoSecondarySkeleton} />
          ) : (
            <p className={styles.infoSecondary}>
              {activeRuns.data?.runs.length
                ? `${activeRuns.data.runs.length} run${activeRuns.data.runs.length === 1 ? "" : "s"} in progress.`
                : "No runs in progress."}
            </p>
          )}
        </PageSection>
      </div>

      <div className={styles.statRow}>
        {statCards.map((stat, index) => (
          <PageSection
            key={stat.key}
            className={cn(styles.statCard, ACCENT_CLASS[stat.accent])}
            aria-label={`${stat.label}, ${activeWindowLabel}`}
            padded
          >
            <p className={styles.statLabel}>
              {stat.label} · {activeWindowLabel}
            </p>
            {isLoading ? (
              <>
                <SkeletonBlock
                  width={`${58 + ((index * 13) % 30)}%`}
                  height={28}
                  className={styles.statValueSkeleton}
                />
                <SkeletonBlock
                  width={`${45 + ((index * 17) % 35)}%`}
                  height={12}
                  className={styles.statAllTimeSkeleton}
                />
              </>
            ) : (
              <>
                <p className={styles.statValue}>{stat.value}</p>
                <p className={styles.statAllTime}>{stat.allTime}</p>
              </>
            )}
          </PageSection>
        ))}
      </div>
    </div>
  );
}
